import { Box, Theme, Typography, makeStyles } from '@material-ui/core';
import { WbTooltip } from '../WbTooltip';
import { CSSProperties, ReactNode, useContext } from 'react';
import { Helmet } from 'react-helmet';

type WbHeaderProps = {
  pageTitleOverride?: string;
  style?: CSSProperties;
  titleStyle?: CSSProperties;
  title: ReactNode;
  tooltip?: string;
  children?: ReactNode;
  breadcrumbs?: ReactNode;
  subtitle?: string;
  headerStyle?: CSSProperties;
  titleContainerStyle?: CSSProperties;
  hidden?: boolean;
};

import { useContent } from '@backstage/core-components';
import { configApiRef, useApi } from '@backstage/core-plugin-api';
import clsx from 'clsx';
import { SidebarConfig, WbSidebarConfigContext } from '../WbSidebar/config';

const useStyles = makeStyles<Theme, { sidebarConfig: SidebarConfig }>(
  theme => ({
    header: {
      gridArea: 'pageHeader',
      width: '100%',
    },
    breadcrumbsWrapper: props => ({
      position: 'fixed',
      zIndex: 10,
      width: '100%',
      [theme.breakpoints.down(props.sidebarConfig.mobileBreakpoint)]: {
        position: 'relative',
      },
    }),
    headerContent: props => ({
      display: 'flex',
      flexWrap: 'wrap',
      gap: theme.spacing(1),
      paddingInline: theme.spacing(1.5),
      alignItems: 'center',
      height: '56px',
      [theme.breakpoints.down(props.sidebarConfig.mobileBreakpoint)]: {
        paddingInline: theme.spacing(1.5),
        paddingBlock: theme.spacing(1.5),
        height: 'auto',
      },
    }),
    headerContentWithBreadcrumbs: props => ({
      marginTop: '32px',
      [theme.breakpoints.down(props.sidebarConfig.mobileBreakpoint)]: {
        marginTop: '0px',
      },
    }),
    headerTitleContainer: {
      flex: 1,
    },
    title: {
      wordBreak: 'break-word',
      marginBottom: 0,
    },
  }),
  { name: 'WbHeader' },
);

type HeaderStyles = ReturnType<typeof useStyles>;

type TitleFragmentProps = {
  classes: HeaderStyles;
  pageTitle: string | ReactNode;
  style?: WbHeaderProps['titleStyle'];
  tooltip?: WbHeaderProps['tooltip'];
};

const TitleFragment = ({
  pageTitle,
  classes,
  tooltip,
  style,
}: TitleFragmentProps) => {
  const { contentRef } = useContent();

  const FinalTitle = (
    <Typography
      ref={contentRef}
      tabIndex={-1}
      className={classes.title}
      variant="h4"
      style={style}
    >
      {pageTitle}
    </Typography>
  );

  if (!tooltip) {
    return FinalTitle;
  }

  return (
    <WbTooltip title={tooltip} placement="top-start">
      {FinalTitle}
    </WbTooltip>
  );
};

/**
 * Witboost main header
 *
 * @public
 *
 */
export const WbHeader = (props: WbHeaderProps) => {
  const {
    children,
    pageTitleOverride,
    style,
    title,
    tooltip,
    breadcrumbs,
    titleStyle,
    headerStyle,
    titleContainerStyle,
    hidden,
  } = props;
  const { sidebarConfig } = useContext(WbSidebarConfigContext);
  const classes = useStyles({ sidebarConfig });
  const configApi = useApi(configApiRef);
  const appTitle = configApi.getOptionalString('app.title') || 'Witboost';
  const documentTitle = pageTitleOverride || title;
  const pageTitle = title || pageTitleOverride;
  const titleTemplate = `${documentTitle} | %s | ${appTitle}`;
  const defaultTitle = `${documentTitle} | ${appTitle}`;

  const helmet = (
    <Helmet titleTemplate={titleTemplate} defaultTitle={defaultTitle} />
  );
  if (hidden) {
    return helmet;
  }

  return (
    <>
      {helmet}
      <header style={style} className={classes.header}>
        {breadcrumbs && (
          <div className={classes.breadcrumbsWrapper}>{breadcrumbs}</div>
        )}
        <Box
          className={clsx(
            classes.headerContent,
            breadcrumbs && classes.headerContentWithBreadcrumbs,
          )}
          style={headerStyle}
        >
          <Box
            className={classes.headerTitleContainer}
            style={titleContainerStyle}
          >
            <TitleFragment
              style={titleStyle}
              classes={classes}
              pageTitle={pageTitle}
              tooltip={tooltip}
            />
          </Box>
          {children}
        </Box>
      </header>
    </>
  );
};
